/**
 * Which units a requirement's picker may offer, and how to explain the ones it
 * withheld.
 *
 * Pure and dependency-free, like the rest of `src/lib/process/`, because
 * `vitest.config.ts` is `environment: 'node'` with `include: ['src/**\/*.test.ts']`
 * — a component test would silently never run, so logic that decides what an
 * operator is allowed to pick does not belong in the `.tsx`.
 */

/** The slice of `AvailabilitySlot` this needs. */
export interface UnitAvailability {
  available: boolean
  reason: 'reservation' | 'maintenance' | 'out_of_service' | 'daily_cap' | null
}

export interface UnitOptionsPartition<T> {
  /** What the dropdown may offer. */
  selectable: T[]
  /** How many of the fleet could take the job — the "N of M free" numerator. */
  freeCount: number
  /** How many were withheld. */
  hiddenCount: number
  /** Withheld because something occupies the window. */
  busyCount: number
  /** Withheld because the unit has run its configured number of times today. */
  cappedCount: number
  /**
   * The parenthetical shown beside the count. Empty when nothing was withheld.
   *
   * The two reasons are reported SEPARATELY and that is the point of this
   * module. A busy unit frees up later the same day; a capped one is standing
   * idle and still cannot take the run until tomorrow. Calling both "busy"
   * sends an operator to wait at a machine that is free in front of them.
   */
  withheldNote: string
}

/**
 * @param options every unit of the requirement's type
 * @param availabilityOf the probe's answer for a unit, or null when no probe
 *   has been made — then nothing is withheld, because "unknown" must not read
 *   as "unavailable"
 * @param heldUnitId the unit this requirement already holds, always kept
 *   visible so a reserved row still names its own machine
 */
export function partitionUnitOptions<T extends { id: string }>(
  options: T[],
  availabilityOf: ((unitId: string) => UnitAvailability | undefined) | null,
  heldUnitId: string | null,
): UnitOptionsPartition<T> {
  const slotOf = (id: string) => (availabilityOf ? availabilityOf(id) : undefined)
  // `!== false` rather than `=== true`: a unit the probe has no answer for is
  // offered, so a partial response never silently empties the list.
  const isFree = (id: string) => slotOf(id)?.available !== false

  const freeCount = availabilityOf ? options.filter((u) => isFree(u.id)).length : options.length
  const selectable = options.filter((u) => u.id === heldUnitId || isFree(u.id))

  const withheldReasons = options
    .filter((u) => u.id !== heldUnitId && slotOf(u.id)?.available === false)
    .map((u) => slotOf(u.id)?.reason ?? null)
  const cappedCount = withheldReasons.filter((x) => x === 'daily_cap').length
  const busyCount = withheldReasons.length - cappedCount

  return {
    selectable,
    freeCount,
    hiddenCount: options.length - selectable.length,
    busyCount,
    cappedCount,
    withheldNote: [
      busyCount > 0 ? `${busyCount} busy at this time` : null,
      cappedCount > 0 ? `${cappedCount} at today's run limit` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }
}
